import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import dayjs from "dayjs";
import { useQueryClient } from "@tanstack/react-query";
import { TutorialJourneyType } from "@growth-machine-llc/stridist-api-client";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import { JourneyProfileInfo } from "../../admin/journey/JourneyProfileInfo";
import { useJoyrideSteps } from "./useJoyrideSteps";
import TutorialJourneyActivatorsService from "../../../services/TutorialJourneyActivatorsService";
import { TUTORIAL_JOURNEY_SINGLE_KEY } from "../../../routes/admin/onboardingTemplate/JourneyTemplate";
import { JourneyThreshold } from "../../../constants";
import { useAnalytics } from "../../../hooks/useAnalytics";

export const useJourneyWithActivator = (
  journeyType: TutorialJourneyType,
  autoStart = false,
  forceShow = false,
) => {
  const queryClient = useQueryClient();
  const {
    steps,
    activatorContent,
    tutorialJourneyLoading,
    journeyActivatorProfile,
    journeyActivatorId,
    journeyActivatorInteractions = [],
    journeyStatus,
  } = useJoyrideSteps(journeyType);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [runJourney, setRunJourney] = useState(false);
  const firstShownAtUtc = useRef<dayjs.Dayjs | null>(null);
  const [trackEvent] = useAnalytics();

  const skipLog = forceShow; // skip API logging whenever cookie-forced
  const hasNotInteracted = journeyActivatorInteractions.length === 0;
  // Only consider interaction gating if an activator exists; otherwise rely on forceShow/autoStart
  const hasActivator = Boolean(journeyActivatorId);
  const shouldShowActivator = forceShow || (hasActivator && hasNotInteracted);

  const contentRef = useRef<HTMLDivElement>(null);

  const sendInteraction = useCallback(
    async (startedJourney: boolean) => {
      if (skipLog) return;
      if (!journeyActivatorId || !firstShownAtUtc.current) return;

      const hasMedia = Boolean(
        contentRef.current?.querySelector("video, iframe"),
      );

      const clickThresholdMs = hasMedia
        ? JourneyThreshold.MEDIA_CLICK_THRESHOLD_MS
        : JourneyThreshold.DEFAULT_CLICK_THRESHOLD_MS;

      await TutorialJourneyActivatorsService.createActivatorInteraction({
        activatorId: journeyActivatorId,
        startedJourney,
        firstShownAtUtc: firstShownAtUtc.current,
        clickThresholdMs,
      });
      queryClient.invalidateQueries({
        queryKey: [TUTORIAL_JOURNEY_SINGLE_KEY, { type: journeyType }],
      });
    },
    [journeyActivatorId, journeyType, queryClient],
  );

  const handleStart = useCallback(async () => {
    setIsModalOpen(false);
    setRunJourney(true);
    firstShownAtUtc.current ??= dayjs.utc();
    await sendInteraction(true);

    if (journeyType === TutorialJourneyType.ONBOARDING) {
      trackEvent("Coach - Start Journey", { accepted: true });
    }
  }, [sendInteraction]);

  const handleSkip = useCallback(async () => {
    setIsModalOpen(false);
    await sendInteraction(false);

    if (journeyType === TutorialJourneyType.ONBOARDING) {
      trackEvent("Coach - Start Journey", { accepted: false });
    }
  }, [sendInteraction]);

  useEffect(() => {
    setIsModalOpen(false);
    setRunJourney(false);
    firstShownAtUtc.current = null;
  }, [journeyType]);

  useEffect(() => {
    if (tutorialJourneyLoading) return;

    if (autoStart) {
      firstShownAtUtc.current ??= dayjs.utc();
      setRunJourney(true);
      return;
    }

    // If we should show an activator and there is one, open it
    if (shouldShowActivator && activatorContent) {
      firstShownAtUtc.current = dayjs.utc();
      setIsModalOpen(true);
      return;
    }

    // If there's no activator:
    // - For forceShow (admin with tutorials cookie), auto-start
    // - Otherwise, do not auto-start (respect normal behavior without cookie)
    if (!activatorContent && forceShow) {
      firstShownAtUtc.current ??= dayjs.utc();
      setRunJourney(true);
      return;
    }
  }, [
    tutorialJourneyLoading,
    activatorContent,
    autoStart,
    shouldShowActivator,
    forceShow,
  ]);

  const ActivatorDialog = useMemo(
    () =>
      shouldShowActivator &&
      activatorContent &&
      !autoStart && (
        <Dialog
          open={isModalOpen}
          onClose={(e, reason) => {
            if (reason !== "backdropClick") handleSkip();
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent>
            {journeyActivatorProfile && (
              <Box mb={2}>
                <JourneyProfileInfo
                  profileName={journeyActivatorProfile.name}
                  profileAvatar={journeyActivatorProfile.avatar || ""}
                  profileSubtitle={journeyActivatorProfile.subtitle || ""}
                />
              </Box>
            )}
            <div ref={contentRef}>{activatorContent}</div>
          </DialogContent>
          <DialogActions
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: 3,
              pb: 2,
            }}
          >
            <Button onClick={handleSkip}>Skip</Button>
            <Button variant="contained" onClick={handleStart}>
              Start Journey
            </Button>
          </DialogActions>
        </Dialog>
      ),
    [
      shouldShowActivator,
      activatorContent,
      isModalOpen,
      journeyActivatorProfile,
      autoStart,
      handleSkip,
      handleStart,
    ],
  );

  return { steps, ActivatorDialog, runJourney, setRunJourney, journeyStatus };
};
